import React, { useCallback, useEffect, useMemo, useState } from "react";
import config from "../config.js";
import { apiErrorMessage } from "../utils/apiError";
import { activeEarmarksByLine, loadPoEarmarks } from "../features/purchaseOrders/poData";

/**
 * Earmark a PO line's material for a sales order.
 *
 * Material bought FOR an order is indistinguishable from stock once it lands.
 * PO 1341 (Boyds, Faith SO-1119) is the case this exists for: its two tube lines
 * can be DERIVED to job 9480 / SO-1119 from live job tasks, but its $296.92 12ga
 * sheet derives nothing — the laser job does not exist yet — so the most
 * valuable piece on the order printed a label naming no owner.
 *
 * An earmark is a real reservation: the API reads this table live inside
 * `_sql_expected_reservations()`, so earmarked material stops being offered to
 * other orders. It releases itself three ways — manually, when the sales order
 * closes, or as the material is consumed by a job on that order.
 *
 * DEFAULT IS OFF. Most material is bought to stock and belongs to nobody; a PO
 * with no earmarks behaves exactly as it always has.
 */
export default function POSalesOrderEarmarks({ poId, poLines, salesOrders, onChanged }) {
  const API = config.API_BASE_URL;
  const [allocations, setAllocations] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyLine, setBusyLine] = useState(null);
  // Per-line draft: { [po_line_id]: { sales_order_id, quantity } }
  const [draft, setDraft] = useState({});
  // "Apply to every material line", for the common special-order PO where the
  // whole order was raised against one job.
  const [bulkSo, setBulkSo] = useState("");

  const load = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    setError("");
    try {
      const data = await loadPoEarmarks(poId); // features/purchaseOrders/poData (the phone's read too)
      setAllocations(data.allocations);
      setSuggestions(data.suggestions);
    } catch (e) {
      setError(e.message || "Could not load earmarks.");
    } finally {
      setLoading(false);
    }
  }, [poId]);

  useEffect(() => { load(); }, [load]);

  // Only material lines can be earmarked — a consumable/service line has no
  // stock to hold.
  const materialLines = useMemo(
    () => (poLines || []).filter(l => l.po_line_id && l.material_id && !l._delete),
    [poLines]
  );

  const allocsByLine = useMemo(() => activeEarmarksByLine(allocations), [allocations]); // poData

  // A hint is only a one-click earmark when its job belongs to a sales order.
  // The API derives demand from every open job, internal stock jobs included:
  // on Ryerson PO 1304 all 180 hints were those, each a button that could only
  // answer "Pick a sales order first." — and laid end to end they crushed the
  // page's PO list.
  const suggestionsByLine = useMemo(() => {
    const m = {};
    (suggestions || []).forEach(s => {
      if (!s.sales_order_id) return;
      (m[s.po_line_id] = m[s.po_line_id] || []).push(s);
    });
    return m;
  }, [suggestions]);

  const openSalesOrders = useMemo(
    () => (salesOrders || []).filter(
      so => !["Cancelled", "Completed", "Closed", "Voided"].includes(so.status)
    ),
    [salesOrders]
  );

  const setDraftField = (lineId, field, value) =>
    setDraft(p => ({ ...p, [lineId]: { ...(p[lineId] || {}), [field]: value } }));

  const save = async (lineId, salesOrderId, quantity, jobId) => {
    if (!salesOrderId) { setError("Pick a sales order first."); return; }
    setBusyLine(lineId);
    setError("");
    try {
      const res = await fetch(`${API}/purchase_orders/lines/${lineId}/allocations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sales_order_id: Number(salesOrderId),
          quantity: Number(quantity) || 1,
          job_id: jobId ? Number(jobId) : null,
        }),
      });
      // The API refuses over-allocation and closed orders with a sentence worth
      // showing verbatim — it names how many are already spoken for.
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setDraft(p => ({ ...p, [lineId]: {} }));
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message || "Could not save the earmark.");
    } finally {
      setBusyLine(null);
    }
  };

  const release = async (allocationId) => {
    setError("");
    try {
      const res = await fetch(`${API}/purchase_orders/allocations/${allocationId}/release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ release_reason: "Released from the purchase order screen" }),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message || "Could not release the earmark.");
    }
  };

  const applyToAll = async () => {
    if (!bulkSo) return;
    for (const ln of materialLines) {
      const already = allocsByLine[ln.po_line_id] || [];
      if (already.length) continue;          // never overwrite a deliberate choice
      const remaining = Number(ln.quantity_ordered) || 0;
      if (remaining > 0) {
        // eslint-disable-next-line no-await-in-loop
        await save(ln.po_line_id, bulkSo, remaining, null);
      }
    }
    setBulkSo("");
  };

  if (!poId) return null;

  return (
    <div className="card mb-3 border-info">
      <div className="card-header bg-info bg-opacity-10 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <strong>Sales Order Earmarks</strong>
          <span className="text-muted ms-2" style={{ fontSize: "0.8rem" }}>
            optional — material bought for a specific order. An earmark reserves it,
            prints on the receiving label, and releases itself when the order closes
            or the material is used.
          </span>
        </div>
        {materialLines.length > 0 && (
          <div className="d-flex align-items-center gap-2">
            <label className="form-label small mb-0 text-nowrap">Whole PO for:</label>
            <select className="form-select form-select-sm" style={{ minWidth: "220px" }}
                    value={bulkSo} onChange={(e) => setBulkSo(e.target.value)}>
              <option value="">— select a sales order —</option>
              {openSalesOrders.map(so => (
                <option key={so.sales_order_id} value={so.sales_order_id}>
                  {so.order_number}{so.customer_name ? ` — ${so.customer_name}` : ""}
                </option>
              ))}
            </select>
            <button className="btn btn-sm btn-outline-info text-nowrap"
                    disabled={!bulkSo} onClick={applyToAll}
                    title="Earmark every material line that has no earmark yet. Lines you have already set are left alone.">
              Apply to all
            </button>
          </div>
        )}
      </div>

      <div className="card-body py-2">
        {error && <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: "0.85rem" }}>{error}</div>}
        {loading && <div className="text-muted small mb-2">Loading…</div>}

        {materialLines.length === 0 ? (
          <div className="text-muted small">No material lines on this order to earmark.</div>
        ) : (
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Material</th>
                <th style={{ width: "70px" }}>Ord</th>
                <th>Earmarked for</th>
                <th style={{ width: "38%" }}>Add an earmark</th>
              </tr>
            </thead>
            <tbody>
              {materialLines.map(ln => {
                const mine = allocsByLine[ln.po_line_id] || [];
                const hints = suggestionsByLine[ln.po_line_id] || [];
                const d = draft[ln.po_line_id] || {};
                const spoken = mine.reduce((a, x) => a + (Number(x.quantity) || 0), 0);
                const free = (Number(ln.quantity_ordered) || 0) - spoken;
                return (
                  <tr key={ln.po_line_id}>
                    <td>
                      <div style={{ fontSize: "0.85rem" }}>
                        <strong>{ln.material_code || `#${ln.material_id}`}</strong>
                      </div>
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        {ln.material_description}
                      </div>
                    </td>
                    <td>{ln.quantity_ordered}</td>
                    <td>
                      {mine.length === 0 ? (
                        <span className="text-muted small">— stock —</span>
                      ) : mine.map(a => (
                        <div key={a.po_line_allocation_id} className="d-flex align-items-center gap-2 mb-1">
                          <span className="badge bg-info text-dark">
                            {a.order_number} × {a.quantity}
                            {a.consumed_quantity > 0 && ` (${a.consumed_quantity} used)`}
                          </span>
                          <span className="small text-muted">{a.customer_name}</span>
                          {a.master_job_num && <span className="small text-muted">job {a.master_job_num}</span>}
                          <button className="btn btn-sm btn-link p-0 text-danger"
                                  style={{ fontSize: "0.75rem" }}
                                  onClick={() => release(a.po_line_allocation_id)}>
                            release
                          </button>
                        </div>
                      ))}
                    </td>
                    <td>
                      {/* Derived demand, one click. It is only ever a SUGGESTION:
                          derivation cannot answer this for material whose job does
                          not exist yet, which is the case the feature exists for. */}
                      {hints.length > 0 && (
                        <div className="mb-1 d-flex flex-wrap gap-1">
                          {hints.map(h => (
                            <button key={`${h.job_id}-${h.sales_order_id}`}
                                    className="btn btn-sm btn-outline-secondary py-0"
                                    style={{ fontSize: "0.72rem" }}
                                    disabled={busyLine === ln.po_line_id}
                                    title={`Job ${h.master_job_num} on ${h.order_number} already needs ${h.derived_qty} of this`}
                                    onClick={() => save(ln.po_line_id, h.sales_order_id,
                                                        Math.min(h.derived_qty, Math.max(free, 1)), h.job_id)}>
                              {h.order_number} · job {h.master_job_num} · needs {h.derived_qty}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="d-flex gap-1">
                        <select className="form-select form-select-sm"
                                value={d.sales_order_id || ""}
                                onChange={(e) => setDraftField(ln.po_line_id, "sales_order_id", e.target.value)}>
                          <option value="">— sales order —</option>
                          {openSalesOrders.map(so => (
                            <option key={so.sales_order_id} value={so.sales_order_id}>
                              {so.order_number}{so.customer_name ? ` — ${so.customer_name}` : ""}
                            </option>
                          ))}
                        </select>
                        <input type="number" min="1" className="form-control form-control-sm"
                               style={{ width: "70px" }}
                               placeholder={String(Math.max(free, 1))}
                               value={d.quantity || ""}
                               onChange={(e) => setDraftField(ln.po_line_id, "quantity", e.target.value)} />
                        <button className="btn btn-sm btn-info text-nowrap"
                                disabled={busyLine === ln.po_line_id || !d.sales_order_id}
                                onClick={() => save(ln.po_line_id, d.sales_order_id,
                                                    d.quantity || Math.max(free, 1), null)}>
                          Earmark
                        </button>
                      </div>
                      {free <= 0 && mine.length > 0 && (
                        <div className="small text-muted mt-1">
                          All {ln.quantity_ordered} spoken for.
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
